import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { faultApi } from '@/api/fault.api';
import { useToasts } from '@/context/ToastContext';
import { ApiError } from '@/lib/errors';
import type { FaultTeamKey, TicketFaultMarks } from '@/types/api';

const ROSTER_TEAMS: { key: FaultTeamKey; label: string }[] = [
  { key: 'sales', label: 'Sales' },
  { key: 'designers', label: 'Designers' },
  { key: 'shipment', label: 'Shipment' },
  { key: 'vendors', label: 'Vendors' }
];

interface Props {
  ticketId: string;
  open: boolean;
  onClose: () => void;
  /** Current marks, used to pre-select the pickers. */
  currentMarks?: TicketFaultMarks;
  customerName?: string | null;
}

/**
 * V4.9.19 — Manual multi-party fault panel. A Solver (on their own ticket) or
 * an Admin selects everyone at fault across Sales / Designers / Shipment /
 * Vendors, plus a Customer toggle. Replace semantics: what you save becomes
 * the ticket's complete fault-mark set. Independent of the AI fault attribution.
 */
export function IssueFaultModal({ ticketId, open, onClose, currentMarks, customerName }: Props) {
  const { push } = useToasts();
  const queryClient = useQueryClient();
  const [sel, setSel] = useState<Record<FaultTeamKey, string[]>>({
    sales: [],
    designers: [],
    shipment: [],
    vendors: []
  });
  const [customer, setCustomer] = useState(false);

  const rosterQuery = useQuery({
    queryKey: ['fault-roster'],
    queryFn: () => faultApi.roster(),
    staleTime: 60_000,
    enabled: open
  });

  // Pre-fill from current marks every time the modal opens.
  useEffect(() => {
    if (!open) return;
    const ids = (key: FaultTeamKey) =>
      (currentMarks?.teams?.[key] ?? [])
        .map((m) => m.member_id)
        .filter((x): x is string => !!x);
    setSel({
      sales: ids('sales'),
      designers: ids('designers'),
      shipment: ids('shipment'),
      vendors: ids('vendors')
    });
    setCustomer(!!currentMarks?.customer);
  }, [open, currentMarks]);

  const save = useMutation({
    mutationFn: () => faultApi.setMarks(ticketId, { ...sel, customer }),
    onSuccess: () => {
      push({ kind: 'success', title: 'Fault updated' });
      void queryClient.invalidateQueries({ queryKey: ['fault-marks', ticketId] });
      void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      onClose();
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'Could not update fault',
        message: err instanceof ApiError ? err.problem?.detail ?? err.message : undefined
      });
    }
  });

  const roster = rosterQuery.data;
  const totalSelected =
    sel.sales.length + sel.designers.length + sel.shipment.length + sel.vendors.length + (customer ? 1 : 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Mark fault — who is responsible?"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending || rosterQuery.isLoading}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : totalSelected ? `Save (${totalSelected})` : 'Save — clear all'}
          </button>
        </>
      }
    >
      <p className="text-small text-muted" style={{ marginTop: 0 }}>
        Select everyone responsible for this ticket — across any teams. This is separate from the AI fault
        attribution and flows straight into Reports.
      </p>
      {rosterQuery.isLoading ? (
        <div className="text-muted">Loading roster…</div>
      ) : roster ? (
        <div className="form-grid">
          {ROSTER_TEAMS.map(({ key, label }) => (
            <MultiSelect
              key={key}
              label={label}
              emptyLabel="None"
              options={(roster[key] ?? []).map((m) => ({ value: m.id, label: m.name }))}
              value={sel[key]}
              onChange={(next) => setSel((s) => ({ ...s, [key]: next }))}
            />
          ))}
        </div>
      ) : (
        <div className="text-muted">Could not load the fault roster.</div>
      )}
      <label className="flex items-center gap-2" style={{ marginTop: 14 }}>
        <input type="checkbox" checked={customer} onChange={(e) => setCustomer(e.target.checked)} />
        <span>Customer at fault{customerName ? ` (${customerName})` : ''}</span>
      </label>
    </Modal>
  );
}
